import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle, WarningCircle, LockSimple, LockSimpleOpen, ClockCounterClockwise, CircleNotch } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };
const thisMonth = () => new Date().toISOString().slice(0, 7);

export function PeriodClose({ businessId }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (m = month) =>
    api.get(`/businesses/${businessId}/close/status`, { params: { month: m } })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { setData(null); setAck(false); setReason(""); load(month); /* eslint-disable-next-line */ }, [businessId, month]);

  const closeMonth = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/businesses/${businessId}/close`, { month, acknowledge_warnings: ack });
      toast.success(res.rowan_message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't close the month."); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    if (!reason.trim()) return toast.error("A reason is required to reopen a closed month.");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/businesses/${businessId}/close/${month}/reopen`, { reason });
      toast.success(res.rowan_message);
      setReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reopen the month."); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm" style={label} data-testid="period-close-loading">Loading…</p>;
  if (data.error) return <p className="text-sm" style={label} data-testid="period-close-error">Couldn't load the close checklist. Try again in a moment.</p>;

  const closed = !!data.close;

  return (
    <div className="space-y-5" data-testid="period-close">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-base font-semibold max-w-2xl" style={{ color: "var(--bk-text)" }} data-testid="period-close-headline">{data.rowan_headline}</p>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="close-month-input"
          className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }} />
      </div>

      <div className="bk-card p-5 space-y-3" data-testid="close-checklist">
        {data.checklist.map((c) => (
          <div key={c.key} className="flex items-center gap-3" data-testid={`close-check-${c.key}`}>
            {c.ok ? <CheckCircle size={20} weight="fill" color="var(--bk-success)" /> : <WarningCircle size={20} weight="fill" color="#E8A838" />}
            <span className="text-sm" style={{ color: c.ok ? "var(--bk-text-2)" : "var(--bk-text)" }}>{c.label}</span>
          </div>
        ))}
        <p className="text-xs pt-2 border-t" style={{ ...label, borderColor: "var(--bk-border)" }}>{data.note}</p>
      </div>

      {closed ? (
        <div className="bk-card p-5" data-testid="close-locked-card" style={{ background: "var(--bk-rowan-bg)" }}>
          <div className="flex items-center gap-2">
            <LockSimple size={20} weight="fill" color="var(--bk-accent)" />
            <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{data.close.period_label} is closed and locked</p>
          </div>
          <p className="text-sm mt-1" style={label}>Closed by {data.close.closed_by_email || data.close.closed_by} on {(data.close.closed_at || "").slice(0, 10)}. Transactions in this month can't be changed while it's locked.</p>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why does this month need to reopen?" data-testid="reopen-reason"
              className="flex-1 min-w-[240px] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <button onClick={reopen} disabled={busy} data-testid="reopen-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <LockSimpleOpen size={15} weight="bold" />} Reopen with reason
            </button>
          </div>
        </div>
      ) : (
        <div className="bk-card p-5" data-testid="close-action-card">
          {data.warnings.length > 0 && (
            <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer" style={{ color: "var(--bk-text)" }}>
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="close-ack-checkbox" />
              Close anyway — record the {data.warnings.length} open item{data.warnings.length !== 1 ? "s" : ""} as known warnings
            </label>
          )}
          <button onClick={closeMonth} disabled={busy || (data.warnings.length > 0 && !ack)} data-testid="close-btn"
            className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <LockSimple size={15} weight="bold" />} Close {data.month}
          </button>
        </div>
      )}

      {data.audit?.length > 0 && (
        <div className="bk-card p-5" data-testid="close-audit">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={18} weight="duotone" color="var(--bk-accent)" />
            <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>Close &amp; reopen history</p>
          </div>
          <div className="space-y-2">
            {data.audit.map((a) => (
              <div key={a.id} className="text-sm flex flex-wrap gap-x-2" data-testid={`close-audit-${a.id}`} style={label}>
                <span className="font-semibold" style={{ color: a.action === "reopened" ? "#B23B1F" : "var(--bk-success)" }}>{a.action === "reopened" ? "Reopened" : "Closed"}</span>
                <span>by {a.by} on {(a.at || "").slice(0, 10)}</span>
                {a.reason && <span className="italic">— “{a.reason}”</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
